import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Steps,
  Card,
  Button,
  Upload,
  Typography,
  Table,
  Tag,
  Alert,
  Progress,
  Space,
  Row,
  Col,
  Statistic,
  Select,
  Tooltip,
  Collapse,
  Modal,
  Result,
  message,
} from 'antd';
import {
  UploadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
  DownloadOutlined,
  ReloadOutlined,
  PlayCircleOutlined,
  FileTextOutlined,
  PictureOutlined,
} from '@ant-design/icons';
import type { RcFile, UploadFile } from 'antd/es/upload';
import { useTranslation } from 'react-i18next';
import {
  parseCsv,
  startJob,
  getJobStatus,
  downloadCsvTemplate,
  exportResultsAsCsv,
  adminUploadListingPhotos,
  type CsvRow,
  type CsvParseResult,
  type BulkUploadJob,
  type JobRowResult,
} from '../api/services/bulkUploadService';

const { Step } = Steps;
const { Title, Text } = Typography;
const { Dragger } = Upload;
const { Option } = Select;

const POLL_INTERVAL_MS = 2500;

interface BulkUploadPageProps {
  isAdmin?: boolean;
  dealerId?: string;
  dealers?: Array<{ id: string; companyName: string; city?: string; email?: string }>;
}

const BulkUploadPage: React.FC<BulkUploadPageProps> = ({
  isAdmin = false,
  dealerId: initialDealerId,
  dealers = [],
}) => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedDealerId, setSelectedDealerId] = useState(initialDealerId ?? '');

  // Step 0 — CSV
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvParsing, setCsvParsing] = useState(false);
  const [parseResult, setParseResult] = useState<CsvParseResult | null>(null);

  // Step 1 — Processing
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<BulkUploadJob | null>(null);
  const [starting, setStarting] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Image upload modal
  const [photoModal, setPhotoModal] = useState<{ listingId: string; label: string } | null>(null);
  const [photoFiles, setPhotoFiles] = useState<UploadFile[]>([]);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoProgress, setPhotoProgress] = useState(0);

  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const startPolling = useCallback((id: string) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const status = await getJobStatus(id);
        setJobStatus(status);
        if (['COMPLETED', 'PARTIAL', 'FAILED'].includes(status.status)) {
          clearInterval(pollRef.current!);
          pollRef.current = null;
        }
      } catch { /* ignore transient errors */ }
    }, POLL_INTERVAL_MS);
  }, []);

  // ─── Step 0: Upload CSV ──────────────────────────────────────────────────────

  const handleCsvBeforeUpload = (file: RcFile): boolean => {
    const valid = file.type === 'text/csv' || file.name.endsWith('.csv') || file.name.endsWith('.xlsx');
    if (!valid) { message.error(t('admin.bulk_upload.msg_csv_only')); return false; }
    setCsvFile(file as unknown as File);
    return false;
  };

  const handleParseCsv = async () => {
    if (!csvFile) return;
    if (isAdmin && !selectedDealerId) { message.error(t('admin.bulk_upload.msg_select_dealer_first')); return; }
    setCsvParsing(true);
    try {
      const result = await parseCsv(csvFile, isAdmin ? selectedDealerId : undefined);
      setParseResult(result);
      setJobId(result.jobId);
      setCurrentStep(1);
      message.success(t('admin.bulk_upload.msg_csv_parsed', { count: result.validRows }));
    } catch (err: any) {
      message.error(err?.message || t('admin.bulk_upload.msg_parse_failed'));
    } finally {
      setCsvParsing(false);
    }
  };

  // ─── Step 1: Review & Start ──────────────────────────────────────────────────

  const handleStart = async () => {
    if (!jobId) return;
    setStarting(true);
    try {
      await startJob(jobId, {});
      const status = await getJobStatus(jobId);
      setJobStatus(status);
      setCurrentStep(2);
      startPolling(jobId);
    } catch (err: any) {
      message.error(err?.message || t('admin.bulk_upload.msg_upload_start_failed'));
    } finally {
      setStarting(false);
    }
  };

  const handleReset = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    setCsvFile(null);
    setParseResult(null);
    setJobId(null);
    setJobStatus(null);
    setCurrentStep(0);
    if (!isAdmin) setSelectedDealerId('');
  };

  // ─── Image modal ─────────────────────────────────────────────────────────────

  const openPhotoModal = (row: JobRowResult) => {
    if (!row.listingId) return;
    setPhotoFiles([]);
    setPhotoProgress(0);
    setPhotoModal({ listingId: row.listingId, label: `${row.make} ${row.model} ${row.year}` });
  };

  const handlePhotoUpload = async () => {
    if (!photoModal || photoFiles.length === 0) return;
    setPhotoUploading(true);
    try {
      const files = photoFiles.map(f => f.originFileObj as File).filter(Boolean);
      await adminUploadListingPhotos(photoModal.listingId, files, setPhotoProgress);
      message.success(t('admin.bulk_upload.msg_photos_uploaded', { count: files.length }));
      setPhotoModal(null);
      setPhotoFiles([]);
    } catch (err: any) {
      message.error(err?.message || t('admin.bulk_upload.msg_photo_upload_failed'));
    } finally {
      setPhotoUploading(false);
      setPhotoProgress(0);
    }
  };

  // ─── Derived state ───────────────────────────────────────────────────────────

  const isProcessing = jobStatus?.status === 'PROCESSING';
  const isDone = jobStatus && ['COMPLETED', 'PARTIAL', 'FAILED'].includes(jobStatus.status);
  const progress = jobStatus
    ? Math.round((jobStatus.processedRows / Math.max(jobStatus.totalRows, 1)) * 100)
    : 0;
  const totalValidRows = parseResult?.validRows ?? 0;

  // ─── CSV preview columns ─────────────────────────────────────────────────────

  const csvColumns = [
    { title: '#', dataIndex: 'rowIndex', width: 50, render: (v: number) => <Text type="secondary">{v + 1}</Text> },
    {
      title: 'Make',
      dataIndex: 'make',
      render: (v: string, row: CsvRow) =>
        row._errors.length > 0 ? (
          <Tooltip title={row._errors.join('; ')}><Text type="danger">{v || '—'}</Text></Tooltip>
        ) : <Text strong>{v}</Text>,
    },
    { title: 'Model', dataIndex: 'model' },
    { title: 'Year', dataIndex: 'year', width: 70 },
    { title: 'Price', dataIndex: 'price', width: 100, render: (v: number) => v ? `€${v.toLocaleString()}` : '—' },
    { title: 'Mileage', dataIndex: 'mileage', width: 90, render: (v?: number) => v ? `${v.toLocaleString()} km` : '—' },
    { title: 'Color', dataIndex: 'color', width: 80 },
    { title: 'Fuel', dataIndex: 'fuel_type', width: 80 },
    {
      title: 'Status',
      key: 'status',
      width: 90,
      render: (_: any, row: CsvRow) =>
        row._errors.length > 0
          ? <Tag color="error" icon={<CloseCircleOutlined />}>Invalid</Tag>
          : <Tag color="success" icon={<CheckCircleOutlined />}>Valid</Tag>,
    },
  ];

  // ─── Results columns ─────────────────────────────────────────────────────────

  const resultColumns = [
    { title: 'Row', dataIndex: 'row', width: 60 },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 90,
      render: (s: string) => {
        const map: Record<string, [string, React.ReactNode]> = {
          success: ['success', <CheckCircleOutlined />],
          failed: ['error', <CloseCircleOutlined />],
          skipped: ['warning', <WarningOutlined />],
        };
        const [color, icon] = map[s] || ['default', null];
        return <Tag color={color} icon={icon}>{s}</Tag>;
      },
    },
    { title: 'Vehicle', key: 'vehicle', render: (_: any, r: JobRowResult) => r.make ? `${r.make} ${r.model} ${r.year}` : '—' },
    { title: 'Reason', dataIndex: 'reason', render: (v?: string) => v ? <Text type="secondary" style={{ fontSize: 12 }}>{v}</Text> : null },
    {
      title: 'Images',
      key: 'images',
      width: 130,
      render: (_: any, r: JobRowResult) =>
        r.status === 'success' && r.listingId ? (
          <Button
            size="small"
            icon={<PictureOutlined />}
            onClick={() => openPhotoModal(r)}
          >
            Add Images
          </Button>
        ) : null,
    },
  ];

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={4} style={{ margin: 0 }}>
          {isAdmin ? t('bulk_user_upload.title_admin') : t('bulk_user_upload.title')}
        </Title>
        <Text type="secondary">
          {t('bulk_user_upload.subtitle')}{' '}
          <Button
            type="link"
            size="small"
            icon={<DownloadOutlined />}
            onClick={downloadCsvTemplate}
            style={{ padding: 0 }}
          >
            {t('bulk_user_upload.download_template')}
          </Button>
        </Text>
      </div>

      <Steps current={currentStep} style={{ marginBottom: 32 }} size="small">
        <Step title={t('bulk_upload_page.step_upload')} icon={<FileTextOutlined />} />
        <Step title={t('bulk_upload_page.step_review')} icon={<CheckCircleOutlined />} />
        <Step title={t('bulk_upload_page.step_results')} icon={<CheckCircleOutlined />} />
      </Steps>

      {/* ── Step 0: Upload CSV ── */}
      {currentStep === 0 && (
        <Card>
          {isAdmin && (
            <div style={{ marginBottom: 20 }}>
              <Text strong>Select Dealer *</Text>
              <Select
                showSearch
                placeholder="Choose a dealer to upload for"
                style={{ width: '100%', marginTop: 8 }}
                value={selectedDealerId || undefined}
                onChange={setSelectedDealerId}
                optionFilterProp="label"
                size="large"
              >
                {dealers.map(d => (
                  <Option key={d.id} value={d.id} label={`${d.companyName} ${d.email ?? ''}`}>
                    <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.3' }}>
                      <span style={{ fontWeight: 600 }}>{d.companyName}{d.city ? ` — ${d.city}` : ''}</span>
                      <span style={{ fontSize: 12, color: '#888' }}>{d.email}</span>
                    </div>
                  </Option>
                ))}
              </Select>
            </div>
          )}

          <Dragger
            accept=".csv,.xlsx"
            beforeUpload={handleCsvBeforeUpload}
            showUploadList={false}
            disabled={csvParsing}
            style={{ marginBottom: 16 }}
          >
            <p className="ant-upload-drag-icon">
              <UploadOutlined style={{ fontSize: 40, color: '#fe5a1d' }} />
            </p>
            <p className="ant-upload-text" style={{ fontWeight: 600 }}>{t('bulk_user_upload.drop_csv')}</p>
            <p className="ant-upload-hint">{t('bulk_user_upload.supports_csv')}</p>
            {csvFile && (
              <Tag color="blue" style={{ marginTop: 8 }}>
                {csvFile.name} ({(csvFile.size / 1024).toFixed(1)} KB)
              </Tag>
            )}
          </Dragger>

          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('bulk_user_upload.required_title')}
            description={
              <Text style={{ fontSize: 12 }}>
                <strong>{t('bulk_user_upload.required_label')}</strong> {t('bulk_user_upload.required_fields')}&nbsp;&nbsp;
                <strong>{t('bulk_user_upload.optional_label')}</strong> {t('bulk_user_upload.optional_fields')}
              </Text>
            }
          />

          <Button
            type="primary"
            size="large"
            disabled={!csvFile || (isAdmin && !selectedDealerId)}
            loading={csvParsing}
            onClick={handleParseCsv}
            icon={<UploadOutlined />}
            style={{ background: '#fe5a1d', borderColor: '#fe5a1d' }}
          >
            Parse & Validate CSV
          </Button>
        </Card>
      )}

      {/* ── Step 1: Review & Start ── */}
      {currentStep === 1 && parseResult && (
        <Card>
          <Row gutter={16} style={{ marginBottom: 20 }}>
            <Col span={6}>
              <Statistic title={t('bulk_upload_page.stat_total_rows')} value={parseResult.totalRows} />
            </Col>
            <Col span={6}>
              <Statistic title={t('bulk_upload_page.stat_valid_rows')} value={parseResult.validRows} valueStyle={{ color: '#3f8600' }} />
            </Col>
            <Col span={6}>
              <Statistic title={t('bulk_upload_page.stat_invalid_rows')} value={parseResult.invalidRows} valueStyle={{ color: parseResult.invalidRows > 0 ? '#cf1322' : undefined }} />
            </Col>
            {!isAdmin && (
              <Col span={6}>
                <Statistic title={t('bulk_upload_page.stat_credits_needed')} value={parseResult.validRows} suffix={t('admin.credit_analytics.suffix_credits')} />
              </Col>
            )}
          </Row>

          {parseResult.csvErrors.length > 0 && (
            <Collapse
              style={{ marginBottom: 16 }}
              items={[{
                key: 'errors',
                label: <Text type="danger"><WarningOutlined /> {parseResult.csvErrors.length} rows have errors (will be skipped)</Text>,
                children: (
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {parseResult.csvErrors.map(e => (
                      <li key={e.row} style={{ marginBottom: 4 }}>
                        <Text>Row {e.row}: </Text>
                        <Text type="danger">{e.errors.join(', ')}</Text>
                      </li>
                    ))}
                  </ul>
                ),
              }]}
            />
          )}

          <Title level={5} style={{ marginBottom: 12 }}>Data Preview (first 5 rows)</Title>
          <Table
            dataSource={parseResult.preview}
            columns={csvColumns}
            rowKey="rowIndex"
            pagination={false}
            size="small"
            scroll={{ x: 800 }}
            style={{ marginBottom: 16 }}
            rowClassName={(r: CsvRow) => r._errors.length > 0 ? 'ant-table-row-error' : ''}
          />

          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Images are added per listing after creation"
            description="Once listings are created you will see an 'Add Images' button for each one in the results."
          />

          <Space>
            <Button onClick={() => setCurrentStep(0)}>← Back</Button>
            <Button
              type="primary"
              size="large"
              loading={starting}
              onClick={handleStart}
              disabled={parseResult.validRows === 0}
              icon={<PlayCircleOutlined />}
              style={{ background: '#fe5a1d', borderColor: '#fe5a1d' }}
            >
              Start Upload ({totalValidRows} listing{totalValidRows !== 1 ? 's' : ''})
            </Button>
          </Space>
        </Card>
      )}

      {/* ── Step 2: Processing & Results ── */}
      {currentStep === 2 && (
        <Card>
          {(isProcessing || !isDone) && jobStatus && (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <Progress
                type="circle"
                percent={progress}
                strokeColor="#fe5a1d"
                format={() => `${jobStatus.processedRows}/${jobStatus.totalRows}`}
              />
              <div style={{ marginTop: 20 }}>
                <Title level={4}>Processing your upload…</Title>
                <Text type="secondary">
                  {jobStatus.successCount} succeeded · {jobStatus.failureCount} failed
                </Text>
              </div>
              <div style={{ marginTop: 12 }}>
                <Tag color="processing">Processing</Tag>
                <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                  Auto-refreshing every {POLL_INTERVAL_MS / 1000}s
                </Text>
              </div>
            </div>
          )}

          {isDone && jobStatus && (
            <>
              <Result
                status={jobStatus.status === 'COMPLETED' ? 'success' : jobStatus.status === 'PARTIAL' ? 'warning' : 'error'}
                title={jobStatus.status === 'COMPLETED' ? 'Upload Complete!' : jobStatus.status === 'PARTIAL' ? 'Partial Upload' : 'Upload Failed'}
                subTitle={jobStatus.status === 'PARTIAL' ? 'Some rows could not be processed (e.g. credit limit reached)' : undefined}
              />

              <Row gutter={16} style={{ marginBottom: 20 }}>
                <Col span={8}>
                  <Statistic title="Succeeded" value={jobStatus.successCount} valueStyle={{ color: '#3f8600' }} prefix={<CheckCircleOutlined />} />
                </Col>
                <Col span={8}>
                  <Statistic title="Failed / Skipped" value={jobStatus.failureCount} valueStyle={{ color: jobStatus.failureCount > 0 ? '#cf1322' : undefined }} prefix={<CloseCircleOutlined />} />
                </Col>
                <Col span={8}>
                  <Statistic title="Total Processed" value={jobStatus.processedRows} />
                </Col>
              </Row>

              {jobStatus.results && jobStatus.results.length > 0 && (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                    <Title level={5} style={{ margin: 0 }}>
                      Row Results
                      <Text type="secondary" style={{ fontSize: 13, fontWeight: 400, marginLeft: 12 }}>
                        Click "Add Images" on any listing to upload photos
                      </Text>
                    </Title>
                    {jobStatus.results.some(r => r.status !== 'success') && (
                      <Button size="small" icon={<DownloadOutlined />} onClick={() => exportResultsAsCsv(jobStatus.results!)}>
                        Export Errors CSV
                      </Button>
                    )}
                  </div>
                  <Table
                    dataSource={jobStatus.results}
                    columns={resultColumns}
                    rowKey="row"
                    size="small"
                    pagination={{ pageSize: 20 }}
                    style={{ marginBottom: 16 }}
                  />
                </>
              )}

              <Button
                type="primary"
                onClick={handleReset}
                icon={<ReloadOutlined />}
                style={{ background: '#fe5a1d', borderColor: '#fe5a1d' }}
              >
                Start New Upload
              </Button>
            </>
          )}
        </Card>
      )}

      {/* ── Add Images Modal ── */}
      <Modal
        open={!!photoModal}
        title={`Add Images — ${photoModal?.label ?? ''}`}
        onCancel={() => { setPhotoModal(null); setPhotoFiles([]); }}
        onOk={handlePhotoUpload}
        okText={photoUploading ? 'Uploading…' : `Upload ${photoFiles.length} Photo(s)`}
        okButtonProps={{ disabled: photoFiles.length === 0 || photoUploading, style: { background: '#fe5a1d', borderColor: '#fe5a1d' } }}
        confirmLoading={photoUploading}
        width={520}
      >
        <Upload
          listType="picture-card"
          fileList={photoFiles}
          onChange={({ fileList }) => setPhotoFiles(fileList)}
          beforeUpload={() => false}
          accept="image/*"
          multiple
        >
          {photoFiles.length < 15 && (
            <div>
              <PictureOutlined style={{ fontSize: 20 }} />
              <div style={{ marginTop: 8 }}>{t('bulk_upload_page.select_photos')}</div>
            </div>
          )}
        </Upload>
        {photoUploading && <Progress percent={photoProgress} strokeColor="#fe5a1d" style={{ marginTop: 12 }} />}
        <Text type="secondary" style={{ fontSize: 12 }}>Max 15 photos per listing.</Text>
      </Modal>
    </div>
  );
};

export default BulkUploadPage;
